import { Grid } from "@mui/material";
import { FormControl, FormLabel, Typography } from "@mui/joy";
import { InputText } from "primereact/inputtext";
import MultiDropdownApi from "../../../components/MultiDropdownApi";
import { Card } from "primereact/card";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useSiteContext } from "../../../context/siteContext";
import {
  Checkbox,
  Input,
  MultiSelect,
  Calendar,
  LocationPicker,
} from "../../../components/Shared/Forms";
import { Industry } from "../../../models/site-setup.interface";
import useApiService from "../../../hooks/useApiService";
import { useToast } from "../../../context/ToastContext";
import styles from "./site-details.module.css";
import ButtonPanel from "../../../components/Shared/ButtonPanel";
import { Button } from "primereact/button";
import { Site } from "../../../models/site.interface";
import { capitaliseFirstLetter, snakeToHuman } from "../../../utils/generalUtils";
import { AddressSearch } from "../../../components/Shared/Address/AddressSearch";

export type formDataType = {
  name: string;
  industry: number[];
  start_at?: Date | string;
  end_at?: Date | string;
  address: string;
  gps_latitude: string | number | null;
  gps_longitude: string | number | null;
  recurrence_enabled: boolean;
};

export default function SiteDetails() {
  const { site, setSite } = useSiteContext();
  const toast = useToast();
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [somethingChanged, setSomethingChanged] = useState<boolean>(false);
  const [allOption, setAllOption] = useState<Industry>();
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState<formDataType>({
    name: "",
    industry: [],
    start_at: "",
    end_at: "",
    address: "",
    gps_latitude: null,
    gps_longitude: null,
    recurrence_enabled: false,
  });
  const [gps, setGps] = useState<string>("");
  const [location, setLocation] = useState<{ lat: number; lng: number } | undefined>(undefined);

  const requiredFields = ["name", "industry", "start_at"];

  const { loading: loadingIndustries } = useApiService<Industry[]>({
    url: "/industry",
    method: "GET",
    apiName: "core",
    onSuccess: data => {
      setIndustries(data);
      const allInd = data.find(ind => ind.name.toLowerCase() === "all");
      if (allInd) setAllOption(allInd);
    },
    onError: error => {
      toast("error", error.message || "Error fetching industries");
    },
    fetchOnMount: true,
  });

  const { loading: updatingSite, fetchData: updateSite } = useApiService<Site>({
    fetchOnMount: false,
    url: `/site-list/${site?.id}`,
    method: "PUT",
    onSuccess: data => {
      toast("success", "Site updated successfully.");
      setSite(data);
      setSomethingChanged(false);
    },
  });

  useEffect(() => {
    if (site) {
      setFormData({
        name: site.name,
        industry: site.industry,
        start_at: site.start_at ? new Date(site.start_at) : undefined,
        end_at: site.end_at ? new Date(site.end_at) : undefined,
        address: site.address,
        gps_latitude: site.gps_latitude,
        gps_longitude: site.gps_longitude,
        recurrence_enabled: site.recurrence_enabled,
      });
      setGps(`${site.gps_latitude},${site.gps_longitude}`);
      if (
        site.gps_latitude != null &&
        site.gps_longitude != null &&
        site.gps_latitude !== "" &&
        site.gps_longitude !== ""
      ) {
        const lat =
          typeof site.gps_latitude === "number"
            ? site.gps_latitude
            : parseFloat(String(site.gps_latitude));
        const lng =
          typeof site.gps_longitude === "number"
            ? site.gps_longitude
            : parseFloat(String(site.gps_longitude));
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setLocation({ lat, lng });
        }
      }
    }
  }, [site]);

  function cancelChanges() {
    setFormData({
      name: site?.name || "",
      industry: site?.industry || [],
      start_at: site?.start_at ? new Date(site?.start_at) : undefined,
      end_at: site?.end_at ? new Date(site?.end_at) : undefined,
      address: site?.address || "",
      gps_latitude: site?.gps_latitude || "",
      gps_longitude: site?.gps_longitude || "",
      recurrence_enabled: site?.recurrence_enabled || false,
    });
    setSomethingChanged(false);
    setFieldErrors({});
  }

  async function handleSubmit() {
    // validate fields and set errors
    const errors: { [key: string]: string } = {};

    requiredFields.forEach(field => {
      if (field === "industry") {
        if (!formData.industry || formData.industry.length === 0) {
          errors[field] = "Industry is required";
        }
      } else if (
        !formData[field as keyof formDataType] ||
        formData[field as keyof formDataType] === ""
      ) {
        errors[field] = `${capitaliseFirstLetter(snakeToHuman(field))} is required`;
      }
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const sitePayload = {
      ...site,
      ...formData,
      gps_latitude: gps.split(",")[0],
      gps_longitude: gps.split(",")[1],
    };

    delete sitePayload.industry_list;
    delete sitePayload.job_task_list;
    delete sitePayload.setup_complete;
    delete sitePayload.badge;
    delete sitePayload.updated_at;
    delete sitePayload.created_at;
    delete sitePayload.deleted_at;

    await updateSite({
      payload: sitePayload,
    });
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;

    // Clear any existing error for this field
    if (fieldErrors[name]) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }

    setSomethingChanged(true);

    setFormData({
      ...formData,
      [name]: value,
    });
  }

  function selectIndustry(item: { label: string; value: number }, fieldName: string) {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
    setSomethingChanged(true);

    const industries = formData?.industry || [];

    // check if industryId contains any new ids that are not in industries
    const isNewIndustry = industries.includes(item.value);
    if (isNewIndustry) {
      // remove the industry id from industries
      const updatedIndustries = industries.filter(id => item.value !== id);
      setFormData({
        ...formData,
        industry: updatedIndustries,
      });
      return;
    }
    // check if industryId is the "all" option
    if (item.label === "All") {
      setFormData({
        ...formData,
        industry: [item.value],
      });
      return;
    }

    const isAllSelected = industries.includes(allOption?.id || -1);
    if (isAllSelected) {
      // remove the "all" option from industries
      const updatedIndustries = industries.filter(id => id !== allOption?.id);
      setFormData({
        ...formData,
        industry: [...updatedIndustries, item.value],
      });
      return;
    }

    setFormData({
      ...formData,
      industry: [...industries, ...[item.value]],
    });
  }

  function selectAllIndustries(val: any) {
    setFormData({
      ...formData,
      industry: val,
    });
    if (fieldErrors.industry) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors.industry;
        return newErrors;
      });
    }
    setSomethingChanged(true);
  }

  return (
    <>
      <Typography level="h2" component="h1">
        Details: {site?.name}
      </Typography>
      <Card>
        <Input
          label={"Name"}
          name={"name"}
          value={formData.name}
          onChange={handleInputChange}
          error={fieldErrors["name"]}
        />
        <MultiSelect
          label={"Industry"}
          options={industries.map(industry => ({
            label: industry.name,
            value: industry.id,
          }))}
          loading={loadingIndustries}
          onChange={(val: any) => {
            selectIndustry(val.selectedOption as { label: string; value: number }, "industry");
          }}
          value={formData.industry}
          required={true}
          error={fieldErrors?.industry}
          onSelectAll={(val: any) => {
            selectAllIndustries(val);
          }}
        />
        <div className={styles["responsive-grid-2"]}>
          <Calendar
            label={"Expected start date"}
            name={"start_at"}
            value={formData.start_at}
            onChange={handleInputChange}
            error={fieldErrors?.start_at}
          />
          <div>
            <Calendar
              label={"Expected end date"}
              name={"end_at"}
              value={formData.end_at}
              onChange={handleInputChange}
              error={fieldErrors?.end_at}
            />
            <Checkbox
              checked={formData.end_at === "" || formData.end_at === undefined}
              label={"End date not known"}
              onChange={e => {
                if (e.checked) {
                  setFormData({
                    ...formData,
                    end_at: "",
                  });
                } else if (formData.end_at === "") {
                  const oneMonthFromNow = new Date();
                  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

                  setFormData({
                    ...formData,
                    end_at: oneMonthFromNow,
                  });
                }
              }}
            />
          </div>
        </div>
        <div>
          <Checkbox
            checked={formData.recurrence_enabled}
            label={"Enable recurring daily task plan"}
            onChange={e => {
              setSomethingChanged(true);
              setFormData({
                ...formData,
                recurrence_enabled: e.checked,
              });
            }}
            subLabel={
              "When enabled, each new daily task plan will automatically copy tasks from the previous day."
            }
          />
        </div>
        <AddressSearch
          initialValue={formData.address}
          error={fieldErrors?.address}
          includeMap={true}
          handleChange={event => {
            if (fieldErrors?.address) {
              setFieldErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors?.address;
                return newErrors;
              });
            }
            setSomethingChanged(true);
            setFormData({
              ...formData,
              address: event.address,
              gps_latitude: event.location.lat,
              gps_longitude: event.location.lng,
            });
          }}
          location={location}
        />
      </Card>
      <ButtonPanel inModal={false}>
        <Button
          label={"Cancel"}
          outlined={true}
          disabled={!somethingChanged || updatingSite}
          onClick={cancelChanges}
        />
        <Button
          label={"Save"}
          loading={updatingSite}
          disabled={updatingSite || !somethingChanged}
          onClick={async () => {
            await handleSubmit();
          }}
        />
      </ButtonPanel>
    </>
  );
}
